/**
 * Guided-tour store — état des tours guidés (driver.js).
 *
 * @remarks
 * Deux états :
 *  - `activeTourId` — **éphémère, NON persisté** : le tour en cours (ou `null`).
 *    Un tour n'est donc jamais relancé tout seul au chargement de la page.
 *  - `progress` — **persisté** : la dernière étape atteinte par tour, pour
 *    reprendre là où l'utilisateur l'a quitté.
 *
 * Le rendu du tour vit dans `app/guided-tour` ; ce store ne fait que porter
 * l'état. Pattern persist calqué sur `use-theme-store`.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { TourId } from '@/app/guided-tour/tour-types';

type TourProgress = Partial<Record<TourId, number>>;

interface GuidedTourState {
  /** Tour en cours — éphémère (jamais persisté → `null` à chaque chargement). */
  activeTourId: TourId | null;
  /** Dernière étape atteinte, par tour — persistée, pour reprendre où on s'est arrêté. */
  progress: TourProgress;
  setActiveTourId: (tourId: TourId | null) => void;
  setProgress: (tourId: TourId, index: number) => void;
  /** Remet le tour en cours et toute la progression à zéro. */
  reset: () => void;
}

/** Seule `progress` est persistée. */
type PersistedGuidedTour = Pick<GuidedTourState, 'progress'>;

/** Shape persisté en v2 : la progression d'un unique tour (l'évaluation). */
interface PersistedGuidedTourV2 {
  lastStepIndex?: unknown;
}

const DEFAULTS: PersistedGuidedTour & Pick<GuidedTourState, 'activeTourId'> = {
  activeTourId: null,
  progress: {},
};

export const useGuidedTourStore = create<GuidedTourState>()(
  devtools(
    persist(
      (set) => ({
        ...DEFAULTS,
        setActiveTourId: (activeTourId) => {
          set({ activeTourId });
        },
        setProgress: (tourId, index) => {
          set((state) => ({ progress: { ...state.progress, [tourId]: index } }));
        },
        reset: () => {
          set({ ...DEFAULTS });
        },
      }),
      {
        name: 'saas-front-v2:guided-tour',
        version: 3,
        // `activeTourId` est volontairement exclu → aucun tour en cours au boot.
        partialize: (state) => ({ progress: state.progress }),
        migrate: (persistedState: unknown, oldVersion: number): PersistedGuidedTour => {
          // v1 persistait { enabled, hasSeenTour } : rien à reprendre.
          if (oldVersion < 2) return { progress: {} };
          // v2 persistait { lastStepIndex } pour le seul tour existant (l'évaluation).
          if (oldVersion < 3) {
            const { lastStepIndex } = persistedState as PersistedGuidedTourV2;
            const index = typeof lastStepIndex === 'number' ? lastStepIndex : 0;
            return { progress: index > 0 ? { evaluation: index } : {} };
          }
          return persistedState as PersistedGuidedTour;
        },
      },
    ),
    { name: 'guided-tour', enabled: import.meta.env.DEV },
  ),
);
